/**
 * Withdrawal Review Panel
 * Slide-in review panel for a single withdrawal request. Shows the user, their
 * KYC status, the amount, the payout target (bank holder / IBAN) and the
 * created date, plus a Source-of-Funds section, and lets a master admin
 * Approve or Reject the request.
 *
 * Reuses the existing UserDetailPanel slide-in styling (.detail-* classes) and
 * the ConfirmModal styling (.modal-* / .button-* classes) so it matches the
 * panel's dark glass design language — no new visual style is introduced.
 *
 * Approve / Reject hit the already-existing backend endpoints via apiService:
 *   POST /api/v1/admin/withdrawals/:id/approve
 *   POST /api/v1/admin/withdrawals/:id/reject
 */

import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/apiService';
import { API_CONFIG } from '../../config/api';
import './UserDetailPanel.css';

export interface ReviewWithdrawal {
  id: string;
  userId: string;
  amount: number;
  /** ISO-4217 the payout leaves in. Falls back to GBP if the backend omits it. */
  currency?: string;
  status: string;
  bankIban: string | null;
  bankHolder: string | null;
  /** Skrill/Neteller payout target (null/absent for bank transfers). */
  payoutMethod?: string | null;
  payoutEmail?: string | null;
  userEmail: string;
  username: string;
  createdAt: string;
}

/** A single wallet transaction, as returned by GET /admin/users/:id/transactions. */
interface SourceTransaction {
  id: string;
  type: string;
  amount: number; // pence/cents; positive = credit, negative = debit
  currency: string;
  referenceType: string | null;
  description: string | null;
  createdAt: string;
}

interface WithdrawalReviewPanelProps {
  withdrawal: ReviewWithdrawal | null;
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful approve/reject so the parent can refresh the list. */
  onActionComplete?: (action: 'approved' | 'rejected') => void;
}

interface UserContext {
  email: string;
  username: string;
  kyc_status?: 'none' | 'pending' | 'approved' | 'rejected' | 'resubmission_required';
  country?: string | null;
  created_at: string;
  frozen_at: string | null;
}

interface KYCImages {
  faceImageUrl: string | null;
  idFrontImageUrl: string | null;
  idBackImageUrl: string | null;
}

const kycClass = (status?: string): string => {
  switch (status) {
    case 'approved':
      return 'green';
    case 'rejected':
      return 'red';
    case 'pending':
    case 'resubmission_required':
      return 'yellow';
    default:
      return 'yellow';
  }
};

const kycLabel = (status?: string): string => {
  switch (status) {
    case 'approved':
      return 'verified';
    case 'rejected':
      return 'rejected';
    case 'pending':
      return 'pending';
    case 'resubmission_required':
      return 'resubmission';
    default:
      return 'not verified';
  }
};

const resolveImg = (url: string): string =>
  url.startsWith('http') ? url : `${API_CONFIG.baseURL}${url}`;

// Symbols for the currencies the platform actually uses; anything else falls
// back to the ISO code as a prefix (e.g. "CAD 12.34"). No new design tokens.
const CURRENCY_SYMBOL: Record<string, string> = {
  GBP: '£',
  EUR: '€',
  USD: '$',
  CAD: 'CA$',
};

/** Format minor units (pence/cents) in the given currency. */
const formatMoney = (minorUnits: number, currency?: string): string => {
  const code = (currency || 'GBP').toUpperCase();
  const sym = CURRENCY_SYMBOL[code];
  const value = (minorUnits / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return sym ? `${sym}${value}` : `${code} ${value}`;
};

// Human label for a wallet_transactions.type value (read-only display).
const TX_LABEL: Record<string, string> = {
  deposit: 'Deposit',
  withdrawal: 'Withdrawal',
  bet_stake: 'Bet stake',
  bet_win: 'Bet win',
  bet_refund: 'Bet refund',
  bet_fee: 'Bet fee',
  reserve_lock: 'Reserve lock',
  reserve_unlock: 'Reserve unlock',
  reserve_convert: 'Reserve convert',
  bonus_credit: 'Bonus credit',
  bonus_expire: 'Bonus expire',
  adjustment: 'Adjustment',
  referral_bonus: 'Referral bonus',
  achievement_reward: 'Achievement reward',
  campaign_bonus: 'Campaign bonus',
};
const txLabel = (type: string): string => TX_LABEL[type] ?? type;

const PAYOUT_METHOD_LABEL: Record<string, string> = {
  bank_transfer: 'Bank transfer',
  neteller: 'Neteller',
  skrill: 'Skrill',
  paysafe_card: 'Paysafe card',
};
const payoutMethodLabel = (m?: string | null): string =>
  m ? PAYOUT_METHOD_LABEL[m] ?? m : '—';

const WithdrawalReviewPanel: React.FC<WithdrawalReviewPanelProps> = ({
  withdrawal,
  isOpen,
  onClose,
  onActionComplete,
}) => {
  const [userCtx, setUserCtx] = useState<UserContext | null>(null);
  const [kycImages, setKycImages] = useState<KYCImages | null>(null);
  const [transactions, setTransactions] = useState<SourceTransaction[] | null>(null);
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [processing, setProcessing] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [confirmReject, setConfirmReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const userId = withdrawal?.userId ?? null;

  const loadContext = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    setKycImages(null);
    try {
      const { user } = await apiService.getUserById(userId);
      setUserCtx({
        email: user.email,
        username: user.username,
        kyc_status: user.kyc_status,
        country: (user as { country?: string | null }).country ?? null,
        created_at: user.created_at,
        frozen_at: user.frozen_at,
      });

      // KYC documents are the closest thing to "proof" available to admin today.
      // getKYCDetails returns images only once a submission exists; ignore failures.
      try {
        const kyc = await apiService.getKYCDetails(userId);
        setKycImages(kyc.images);
      } catch {
        setKycImages(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user context');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Source of funds: the user's wallet transactions (deposits = where the
  // withdrawn money came from). Loaded independently so a transactions error
  // never blanks the rest of the review.
  const loadTransactions = useCallback(async () => {
    if (!userId) return;
    setTxLoading(true);
    setTxError(null);
    try {
      const { transactions: rows } = await apiService.getUserTransactions(userId, { limit: 100 });
      setTransactions(rows);
    } catch (err) {
      setTransactions(null);
      setTxError(err instanceof Error ? err.message : 'Failed to load transactions');
    } finally {
      setTxLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen && userId) {
      loadContext();
      loadTransactions();
    } else if (!isOpen) {
      setUserCtx(null);
      setKycImages(null);
      setTransactions(null);
      setTxError(null);
      setError(null);
      setConfirmApprove(false);
      setConfirmReject(false);
      setRejectReason('');
      setProcessing(false);
    }
  }, [isOpen, userId, loadContext, loadTransactions]);

  const handleApprove = async () => {
    if (!withdrawal || processing) return;
    setProcessing(true);
    setError(null);
    try {
      await apiService.approveWithdrawal(withdrawal.id);
      setConfirmApprove(false);
      onActionComplete?.('approved');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve withdrawal');
      setConfirmApprove(false);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!withdrawal || processing) return;
    setProcessing(true);
    setError(null);
    try {
      await apiService.rejectWithdrawal(withdrawal.id, rejectReason.trim() || undefined);
      setConfirmReject(false);
      setRejectReason('');
      onActionComplete?.('rejected');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject withdrawal');
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen || !withdrawal) return null;

  // The withdrawal amount is already in MAJOR units (the list divides cents/100),
  // so format it directly with the payout currency (no longer hardcoded £).
  const wdCurrency = (withdrawal.currency || 'GBP').toUpperCase();
  const wdSym = CURRENCY_SYMBOL[wdCurrency];
  const wdValue = (withdrawal.amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const amount = wdSym ? `${wdSym}${wdValue}` : `${wdCurrency} ${wdValue}`;
  const created = withdrawal.createdAt
    ? new Date(withdrawal.createdAt).toLocaleString()
    : '—';
  // Nuvei withdrawals queue as 'pending_admin' (migration 360); legacy ones as
  // 'pending'. Both are awaiting admin approval → both must be approvable here.
  const isPending = withdrawal.status === 'pending' || withdrawal.status === 'pending_admin';

  // Source-of-funds breakdown: deposits are the money that funded the balance.
  const deposits = (transactions ?? []).filter((t) => t.type === 'deposit');
  const totalDepositsMinor = deposits.reduce((sum, t) => sum + (t.amount || 0), 0);
  const depositCurrency = deposits[0]?.currency ?? wdCurrency;
  // E-wallet payout (Skrill/Neteller) shows method + email; bank shows holder/IBAN.
  const isEwalletPayout =
    withdrawal.payoutMethod === 'neteller' || withdrawal.payoutMethod === 'skrill';
  const hasKycImages =
    kycImages &&
    (kycImages.faceImageUrl || kycImages.idFrontImageUrl || kycImages.idBackImageUrl);

  return (
    <>
      <div className={`detail-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} />
      <div className={`detail-panel ${isOpen ? 'open' : ''}`}>
        <div className="detail-head">
          <span className="detail-title">Review Withdrawal</span>
          <button className="detail-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="detail-body">
          {error && (
            <div
              style={{
                padding: '12px 16px',
                marginBottom: 20,
                background: 'rgba(255, 107, 107, 0.1)',
                border: '1px solid rgba(255, 107, 107, 0.3)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--red)',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          {/* Amount headline */}
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ fontSize: 36, fontWeight: 800 }} className="amount neg">
              {amount}
            </div>
            <div style={{ marginTop: 10 }}>
              <span className={`status status-${withdrawal.status}`}>{withdrawal.status}</span>
            </div>
          </div>

          {/* User */}
          <div className="detail-section">
            <div className="detail-label">👤 User</div>
            <div className="detail-row">
              <label>Username</label>
              <span>{userCtx?.username || withdrawal.username || '—'}</span>
            </div>
            <div className="detail-row">
              <label>Email</label>
              <span>{userCtx?.email || withdrawal.userEmail || '—'}</span>
            </div>
            <div className="detail-row">
              <label>User ID</label>
              <span className="mono" style={{ fontSize: 11 }}>
                {withdrawal.userId}
              </span>
            </div>
            <div className="detail-row">
              <label>Country</label>
              <span>{userCtx?.country || '—'}</span>
            </div>
            <div className="detail-row">
              <label>Account</label>
              <span>
                {loading ? '…' : userCtx?.frozen_at ? 'frozen' : 'active'}
              </span>
            </div>
          </div>

          {/* KYC */}
          <div className="detail-section">
            <div className="detail-label">🪪 KYC</div>
            <div className="detail-row">
              <label>Status</label>
              <span>
                {loading ? (
                  '…'
                ) : (
                  <span className={`status ${kycClass(userCtx?.kyc_status)}`}>
                    {kycLabel(userCtx?.kyc_status)}
                  </span>
                )}
              </span>
            </div>
            {hasKycImages ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
                {kycImages?.faceImageUrl && (
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: 8,
                        fontSize: 12,
                        color: 'var(--text-muted)',
                      }}
                    >
                      Face Photo
                    </label>
                    <img
                      src={resolveImg(kycImages.faceImageUrl)}
                      alt="Face"
                      style={{
                        width: '100%',
                        maxWidth: 300,
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--glass-border)',
                      }}
                    />
                  </div>
                )}
                {kycImages?.idFrontImageUrl && (
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: 8,
                        fontSize: 12,
                        color: 'var(--text-muted)',
                      }}
                    >
                      ID Front
                    </label>
                    <img
                      src={resolveImg(kycImages.idFrontImageUrl)}
                      alt="ID Front"
                      style={{
                        width: '100%',
                        maxWidth: 300,
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--glass-border)',
                      }}
                    />
                  </div>
                )}
                {kycImages?.idBackImageUrl && (
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: 8,
                        fontSize: 12,
                        color: 'var(--text-muted)',
                      }}
                    >
                      ID Back
                    </label>
                    <img
                      src={resolveImg(kycImages.idBackImageUrl)}
                      alt="ID Back"
                      style={{
                        width: '100%',
                        maxWidth: 300,
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--glass-border)',
                      }}
                    />
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Payout target */}
          <div className="detail-section">
            <div className="detail-label">🏦 Payout Target</div>
            <div className="detail-row">
              <label>Method</label>
              <span>{payoutMethodLabel(withdrawal.payoutMethod)}</span>
            </div>
            {isEwalletPayout ? (
              <div className="detail-row">
                <label>Payout Email</label>
                <span className="mono" style={{ fontSize: 12 }}>
                  {withdrawal.payoutEmail || '—'}
                </span>
              </div>
            ) : (
              <>
                <div className="detail-row">
                  <label>Account Holder</label>
                  <span>{withdrawal.bankHolder || '—'}</span>
                </div>
                <div className="detail-row">
                  <label>IBAN</label>
                  <span className="mono" style={{ fontSize: 12 }}>
                    {withdrawal.bankIban || '—'}
                  </span>
                </div>
              </>
            )}
            <div className="detail-row">
              <label>Currency</label>
              <span>{wdCurrency}</span>
            </div>
          </div>

          {/* Source of funds — the user's deposits/transactions fund the balance */}
          <div className="detail-section">
            <div className="detail-label">💰 Source of Funds (Deposit / Transaction History)</div>

            {txLoading && (
              <div className="detail-row">
                <label>Loading</label>
                <span>…</span>
              </div>
            )}

            {!txLoading && txError && (
              <div
                style={{
                  padding: '10px 12px',
                  background: 'rgba(255, 107, 107, 0.08)',
                  border: '1px solid rgba(255, 107, 107, 0.25)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--red)',
                  fontSize: 12,
                }}
              >
                {txError}
              </div>
            )}

            {!txLoading && !txError && transactions && transactions.length === 0 && (
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                No wallet transactions on record for this user.
              </p>
            )}

            {!txLoading && !txError && transactions && transactions.length > 0 && (
              <>
                {/* Deposits headline — the source of the withdrawn money. */}
                <div className="detail-row">
                  <label>Deposits</label>
                  <span>
                    <strong className="amount pos">
                      {formatMoney(totalDepositsMinor, depositCurrency)}
                    </strong>{' '}
                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                      ({deposits.length} {deposits.length === 1 ? 'deposit' : 'deposits'})
                    </span>
                  </span>
                </div>

                {/* Full transaction history, newest first. Deposits emphasised. */}
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {transactions.map((t) => {
                    const isDeposit = t.type === 'deposit';
                    const isCredit = (t.amount || 0) >= 0;
                    return (
                      <div
                        key={t.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'baseline',
                          gap: 12,
                          padding: '8px 10px',
                          background: 'var(--input)',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--glass-border)',
                          ...(isDeposit ? { borderColor: 'var(--green)' } : {}),
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: isDeposit ? 700 : 500 }}>
                            {txLabel(t.type)}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            {new Date(t.createdAt).toLocaleString()}
                            {t.description ? ` · ${t.description}` : ''}
                          </div>
                        </div>
                        <span
                          className={`amount ${isCredit ? 'pos' : 'neg'}`}
                          style={{ fontSize: 13, whiteSpace: 'nowrap' }}
                        >
                          {isCredit ? '+' : '−'}
                          {formatMoney(Math.abs(t.amount || 0), t.currency)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Request meta */}
          <div className="detail-section">
            <div className="detail-label">🧾 Request</div>
            <div className="detail-row">
              <label>Withdrawal ID</label>
              <span className="mono" style={{ fontSize: 11 }}>
                {withdrawal.id}
              </span>
            </div>
            <div className="detail-row">
              <label>Created</label>
              <span>{created}</span>
            </div>
          </div>
        </div>

        <div className="detail-foot">
          <button className="btn btn-secondary" onClick={onClose} disabled={processing}>
            Close
          </button>
          {isPending && (
            <>
              <button
                className="btn btn-danger"
                onClick={() => {
                  setRejectReason('');
                  setConfirmReject(true);
                }}
                disabled={processing}
              >
                Reject
              </button>
              <button
                className="btn btn-success"
                onClick={() => setConfirmApprove(true)}
                disabled={processing}
              >
                Approve
              </button>
            </>
          )}
        </div>
      </div>

      {/* Approve confirm */}
      {confirmApprove && (
        <div className="modal-overlay" onClick={() => !processing && setConfirmApprove(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Approve Withdrawal</h2>
            </div>
            <div className="modal-body">
              <p>
                Approve withdrawal of {amount} for{' '}
                {userCtx?.username || withdrawal.username || withdrawal.userEmail}? This releases the
                payout.
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="button-secondary"
                onClick={() => setConfirmApprove(false)}
                disabled={processing}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-success"
                onClick={handleApprove}
                disabled={processing}
              >
                {processing ? 'Approving…' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject confirm + reason */}
      {confirmReject && (
        <div className="modal-overlay" onClick={() => !processing && setConfirmReject(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reject Withdrawal</h2>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 16 }}>
                Reject withdrawal of {amount} for{' '}
                {userCtx?.username || withdrawal.username || withdrawal.userEmail}?
              </p>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 13 }}>
                Reason (optional)
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--glass-border)',
                  background: 'var(--input)',
                  color: 'inherit',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
              />
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="button-secondary"
                onClick={() => setConfirmReject(false)}
                disabled={processing}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button-danger"
                onClick={handleReject}
                disabled={processing}
              >
                {processing ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WithdrawalReviewPanel;
